import { useLazyQuery } from '@apollo/client';
import type { TariffEvaluatorOutput as SharedTariffEvaluatorOutput } from '@xbcb/api-gateway-client';
import { isRealHts } from '@xbcb/entry-utils/dist/lib/htsUtil';
import type { NamePath } from '@xbcb/ui-types';
import { reportError } from '@xbcb/ui-utils';
import {
  evaluateTariffsDocument,
  ModeOfTransport,
  type TariffEvaluatorInput,
} from '__generated__/graphql';
import { FormInstance } from 'antd';
import { isEvaluatedDutyDisplayEnabled } from 'featureFlags';
import { applyTariffEvaluationToFormLine } from 'libs/applyTariffEvaluation';
import { useCallback, useMemo, useRef, useState } from 'react';
import { resolveEvaluatorLine } from './utils';

interface UseTariffEvaluationArgs {
  form: FormInstance;
  tariffFullNamePath: NamePath;
  originCountryCode: string | undefined;
  arrivalDate: string | undefined;
  modeOfTransport: ModeOfTransport | undefined;
}

export const useTariffEvaluation = ({
  form,
  tariffFullNamePath,
  originCountryCode,
  arrivalDate,
  modeOfTransport,
}: UseTariffEvaluationArgs) => {
  const [evaluateTariffs] = useLazyQuery(evaluateTariffsDocument);
  const [evaluating, setEvaluating] = useState(false);

  const { lineFullNamePath, productFullNamePath, linesPath, lineIndex } =
    useMemo(() => {
      const path = tariffFullNamePath as (string | number)[];
      const linePath = path.slice(0, -1);
      const linesIdx = path.lastIndexOf('lines');
      const linesArrayPath =
        linesIdx >= 0 ? path.slice(0, linesIdx + 1) : linePath;
      const productPath = linesIdx >= 0 ? path.slice(0, linesIdx) : linePath;
      const idx = linesIdx >= 0 ? (path[linesIdx + 1] as number) : 0;
      return {
        lineFullNamePath: linePath,
        productFullNamePath: productPath,
        linesPath: linesArrayPath,
        lineIndex: idx,
      };
    }, [tariffFullNamePath]);

  const argsRef = useRef({ originCountryCode, arrivalDate, modeOfTransport });
  argsRef.current = { originCountryCode, arrivalDate, modeOfTransport };
  const inflightRef = useRef(false);
  const requestTokenRef = useRef(0);
  const queuedHtsRef = useRef<string | undefined>();
  const runRef = useRef<(htsCode: string) => Promise<void>>();

  /**
   * Single invalidation path for every evaluator input: clears each tariff's
   * evaluated `charges` and the line's `cbpCharges` so a stale value is never
   * presented as current. Presence of `charges` therefore means "current".
   * No-op in PROD until the evaluated duty display launches.
   */
  const clearEvaluatedCharges = useCallback(() => {
    if (!isEvaluatedDutyDisplayEnabled) return;
    const tariffs =
      (form.getFieldValue(tariffFullNamePath) as unknown[] | undefined) ?? [];
    form.setFields([
      ...tariffs.map((_, i) => ({
        name: [...(tariffFullNamePath as (string | number)[]), i, 'charges'],
        value: undefined,
      })),
      { name: [...lineFullNamePath, 'cbpCharges'], value: undefined },
    ]);
  }, [form, tariffFullNamePath, lineFullNamePath]);

  const runTariffEvaluation = useCallback(
    async (htsCode: string) => {
      const { originCountryCode, arrivalDate, modeOfTransport } =
        argsRef.current;
      if (!isRealHts(htsCode) || !originCountryCode) return;
      if (inflightRef.current) {
        if (!isEvaluatedDutyDisplayEnabled) return;
        // Never drop the newest input: queue it to run when the active
        // request settles
        queuedHtsRef.current = htsCode;
        requestTokenRef.current += 1;
        clearEvaluatedCharges();
        return;
      }

      const token = ++requestTokenRef.current;
      clearEvaluatedCharges();

      const rawLine = form.getFieldValue(lineFullNamePath) ?? {};
      const product = form.getFieldValue(productFullNamePath) ?? {};
      // See resolveEvaluatorLine for value/assist resolution.
      const line = resolveEvaluatorLine(
        rawLine,
        product,
        isEvaluatedDutyDisplayEnabled,
      );
      const transport =
        arrivalDate || modeOfTransport
          ? {
              ...(arrivalDate && { arrival: { importDate: arrivalDate } }),
              ...(modeOfTransport && { modeOfTransport }),
            }
          : undefined;

      setEvaluating(true);
      inflightRef.current = true;
      try {
        // The returned lines already carry the evaluator's
        // `candidateAdditionalTariffs` (an empty array when the engine returns
        // none), so applying a line both updates its stacking and reconciles
        // its candidate list in a single write.
        const newLines = await applyTariffEvaluationToFormLine({
          line,
          product,
          transport,
          evaluate: async (input) => {
            const { data } = await evaluateTariffs({
              variables: { input: input as unknown as TariffEvaluatorInput },
            });
            return data?.evaluateTariffs as
              | SharedTariffEvaluatorOutput
              | null
              | undefined;
          },
        });

        // A response that does not match the latest request is stale: discard.
        if (token !== requestTokenRef.current) return;

        if (!newLines?.length) return;

        if (newLines.length === 1) {
          form.setFieldValue(lineFullNamePath, newLines[0]);
        } else {
          const allLines =
            (form.getFieldValue(linesPath) as unknown[] | undefined) ?? [];
          form.setFieldValue(linesPath, [
            ...allLines.slice(0, lineIndex),
            ...newLines,
            ...allLines.slice(lineIndex + 1),
          ]);
        }
      } catch (error) {
        // Charges were already cleared, so a failure shows no duty. Report to
        // RUM for UAT/debugging visibility.
        reportError(error instanceof Error ? error : new Error(String(error)));
      } finally {
        inflightRef.current = false;
        setEvaluating(false);
        const queued = queuedHtsRef.current;
        queuedHtsRef.current = undefined;
        if (queued) void runRef.current?.(queued);
      }
    },
    [
      evaluateTariffs,
      form,
      lineFullNamePath,
      productFullNamePath,
      linesPath,
      lineIndex,
      clearEvaluatedCharges,
    ],
  );
  runRef.current = runTariffEvaluation;

  return { runTariffEvaluation, clearEvaluatedCharges, evaluating };
};
